import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion, useAnimation } from "framer-motion";
import { Mail } from "lucide-react";
import ContactModal from "./ContactModal";
import { useNavigate } from "react-router-dom";

/* ------ Import Local Background Image ------ */
import heroBgLocal from "../images/slide1.jpeg";

/* ---------- styled ---------- */
const HeroWrapper = styled.section`
  padding: 0;
  margin: 0;
  position: relative;
  overflow: hidden;
`;

const HeroContainer = styled.div`
  width: 100%;
  min-height: 82vh;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;

  @media (min-width: 900px) {
    min-height: 88vh;
  }
`;

/* ---- Background with gradient + BLUR IMAGE ---- */
const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  background: ${(p) =>
    p.theme.gradients?.servicesBg ||
    `linear-gradient(180deg, ${p.theme.colors?.bgDarker || "#02050A"} 0%, ${
      p.theme.colors?.bgDark || "#0A0F1D"
    } 100%)`};
  overflow: hidden;
  will-change: transform;
  transform-origin: center;
  transition: transform 0.6s ease, filter 0.4s ease;

  /* Parallax From JS */
  transform: translate3d(var(--tx, 0px), var(--ty, 0px), 0) scale(var(--s, 1));

  /* --------- Blur Layer --------- */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url("${(p) => p.imageUrl}");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: blur(4px) brightness(0.7);
    transform: scale(1.12);
    pointer-events: none;
    z-index: -1;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
      60% 45% at 50% 25%,
      rgba(15, 23, 48, 0.08),
      transparent 35%
    ),
    linear-gradient(180deg, rgba(2, 6, 23, 0.12), rgba(2, 6, 23, 0.22));
`;

const Content = styled(motion.div)`
  position: relative;
  z-index: 5;
  max-width: 980px;
  padding: 56px 18px;
  color: ${(p) => p.theme.colors?.textOnDark || "#fff"};
  display: flex;
  flex-direction: column;
  gap: 22px;
  align-items: center;

  @media (min-width: 520px) {
    padding: 64px 28px;
  }
  @media (min-width: 1100px) {
    padding: 80px 32px;
  }
`;

const Eyebrow = styled(motion.div)`
  background: ${(p) => p.theme.colors?.eyebrowBg || "rgba(255,255,255,0.10)"};
  color: ${(p) => p.theme.colors?.eyebrowText || "rgba(255,255,255,0.94)"};
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.7px;
  text-transform: uppercase;
`;

const Title = styled(motion.h1)`
  margin: 0;
  font-weight: 900;
  line-height: 1.08;
  font-size: 1.9rem;
  max-width: 920px;
  color: ${(p) => p.theme.colors?.title || "white"};
  text-shadow: 0 6px 30px rgba(3, 10, 18, 0.45);

  @media (min-width: 520px) {
    font-size: 2.6rem;
  }
  @media (min-width: 900px) {
    font-size: 3.2rem;
  }
`;

const Desc = styled(motion.p)`
  margin: 0;
  max-width: 760px;
  font-size: 1.02rem;
  line-height: 1.6;
  font-weight: 400;
  color: ${(p) => p.theme.colors?.mutedOnDark || "rgba(255,255,255,0.92)"};

  @media (min-width: 900px) {
    font-size: 1.12rem;
  }
`;

const ButtonsRow = styled(motion.div)`
  display: flex;
  gap: 14px;
  margin-top: 6px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Primary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  border-radius: 12px;
  font-weight: 800;
  text-decoration: none;
  background: ${(p) => p.theme.gradients?.cta};
  color: ${(p) => p.theme.colors?.text || "#021025"};
  font-size: 1rem;
  box-shadow: 0 14px 38px rgba(11, 100, 209, 0.22);
  transition: transform 160ms ease, box-shadow 160ms ease, opacity 140ms ease;

  svg {
    flex-shrink: 0;
  }

  &:hover,
  &:focus {
    transform: translateY(-4px);
    box-shadow: 0 22px 54px rgba(11, 100, 209, 0.28);
    outline: none;
  }
`;

const Secondary = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 14px 22px;
  border-radius: 12px;
  background: transparent;
  color: ${(p) =>
    p.theme.colors?.secondaryTextOnBg || "rgba(255,255,255,0.92)"};
  font-weight: 700;
  font-size: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  text-decoration: none;
  transition: transform 140ms ease, background 140ms ease;

  &:hover,
  &:focus {
    background: rgba(255, 255, 255, 0.06);
    transform: translateY(-3px);
    outline: none;
  }
`;

const SubNote = styled(motion.div)`
  margin-top: 6px;
  color: ${(p) => p.theme.colors?.mutedOnDark || "rgba(255,255,255,0.72)"};
  font-size: 0.92rem;
`;

/* ---------- animations ---------- */
const contentVariant = {
  hidden: { opacity: 0, y: 18, scale: 0.995 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] },
  },
};

const eyebrowVariant = {
  hidden: { opacity: 0, y: -8 },
  show: { opacity: 1, y: 0, transition: { delay: 0.05 } },
};

const titleVariant = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { delay: 0.12 } },
};

const descVariant = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { delay: 0.18 } },
};

const buttonsVariant = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { delay: 0.26 } },
};

/* ---------- component ---------- */
export default function Hero({
  imageUrl = heroBgLocal, // 🔥 Local background image
  title = "Modern Debt Recovery Built on Trust, Compliance & Performance",
  description = "We help banks, NBFCs and fintechs recover dues with dignity-based interactions, strict regulatory compliance and clear reporting. A people-first approach for long-term recovery success.",
  primaryCta = { href: "", text: "Work With Us" },
  secondaryCta = { href: "", text: "Contact Us" },
}) {
  const bgRef = useRef(null);
  const controls = useAnimation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    controls.start("show");
  }, [controls]);

  /* --------- Mouse Parallax --------- */
  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    let raf = 0;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / rect.width;
      const dy = (e.clientY - cy) / rect.height;

      const tx = `${dx * 10}px`;
      const ty = `${dy * 8}px`;
      const s = 1.03;

      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--tx", tx);
        el.style.setProperty("--ty", ty);
        el.style.setProperty("--s", s);
      });
    };

    const onLeave = () => {
      cancelAnimationFrame(raf);
      el.style.setProperty("--tx", `0px`);
      el.style.setProperty("--ty", `0px`);
      el.style.setProperty("--s", `1`);
    };

    window.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    el.addEventListener("mouseenter", onMove);

    return () => {
      window.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      el.removeEventListener("mouseenter", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <HeroWrapper aria-labelledby="hero-title" role="region">
      <HeroContainer>
        <HeroBg ref={bgRef} imageUrl={imageUrl} aria-hidden />
        <Overlay aria-hidden />

        <Content
          initial="hidden"
          animate={controls}
          variants={contentVariant}
          role="article"
        >
          <Eyebrow variants={eyebrowVariant} initial="hidden" animate="show">
            Trusted by leading banks & fintechs
          </Eyebrow>

          <Title
            id="hero-title"
            variants={titleVariant}
            initial="hidden"
            animate="show"
          >
            {title}
          </Title>

          <Desc variants={descVariant} initial="hidden" animate="show">
            {description}
          </Desc>

          <ButtonsRow variants={buttonsVariant} initial="hidden" animate="show">
            <Primary
              href={primaryCta.href}
              onClick={() => navigate("/contact")}
            >
              <Mail size={18} />
              {primaryCta.text}
            </Primary>

            <Secondary onClick={() => navigate("/contact")}>
              {secondaryCta.text}
            </Secondary>
          </ButtonsRow>

          <SubNote variants={buttonsVariant} initial="hidden" animate="show">
            Contact us for a free compliance review & pilot program.
          </SubNote>
        </Content>
      </HeroContainer>

      <ContactModal
        open={open}
        onClose={() => setOpen(false)}
        phone="+918510040060"
        email="Ksgm012020@gmail.com"
      />
    </HeroWrapper>
  );
}
